import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import userService from "../services/userService";

const createFallbackProfile = (id) => ({
    name: `User #${id}`,
    username: null,
    avatarUrl: null,
    resolved: false,
});

const normalizeStringId = (id) => {
    if (id == null) {
        return null;
    }
    return String(id).trim();
};

const firstTruthy = (...values) => values.find((value) => typeof value === "string" && value.trim().length > 0) || null;

export default function useUserDirectoryResolver({
    currentUserId = null,
    members = [],
    messages = [],
    userOptions = [],
} = {}) {
    const directoryRef = useRef(new Map());
    const pendingLookupsRef = useRef(new Set());
    const [version, setVersion] = useState(0);

    const bumpVersion = useCallback(() => setVersion((current) => current + 1), []);

    const mergeProfiles = useCallback((entries) => {
        if (!Array.isArray(entries) || entries.length === 0) {
            return;
        }
        const directory = directoryRef.current;
        let mutated = false;

        entries.forEach(([rawId, profile]) => {
            const id = normalizeStringId(rawId);
            if (!id || !profile) {
                return;
            }
            const previous = directory.get(id) || createFallbackProfile(id);
            const next = {
                name: profile.name ?? previous.name ?? null,
                username: profile.username ?? previous.username ?? null,
                avatarUrl: profile.avatarUrl ?? previous.avatarUrl ?? null,
                resolved: Boolean(profile.resolved) || Boolean(previous.resolved),
            };
            const changed = (
                next.name !== previous.name
                || next.username !== previous.username
                || next.avatarUrl !== previous.avatarUrl
                || next.resolved !== previous.resolved
            );
            if (changed) {
                directory.set(id, next);
                mutated = true;
            }
        });

        if (mutated) {
            bumpVersion();
        }
    }, [bumpVersion]);

    const memberProfiles = useMemo(() => {
        const entries = new Map();
        (Array.isArray(members) ? members : []).forEach((member) => {
            const id = normalizeStringId(member?.userId);
            if (!id) {
                return;
            }
            const metadata = {
                name: firstTruthy(
                    member?.displayName,
                    member?.username,
                    member?.name
                ),
                username: typeof member?.username === "string" ? member.username : null,
                avatarUrl: member?.avatarUrl || member?.profilePicture || null,
                resolved: Boolean(member?.displayName || member?.username || member?.name || member?.avatarUrl || member?.profilePicture),
            };
            entries.set(id, metadata);
        });
        return entries;
    }, [members]);

    const memberIds = useMemo(() => Array.from(memberProfiles.keys()), [memberProfiles]);

    const senderMetadata = useMemo(() => {
        const byId = new Map();
        (Array.isArray(messages) ? messages : []).forEach((message) => {
            const id = normalizeStringId(message?.senderId);
            if (!id) {
                return;
            }
            const name = firstTruthy(
                message?.displayName,
                message?.senderName,
                message?.username
            );
            const avatarUrl = message?.avatarUrl || message?.senderAvatarUrl || null;
            const resolved = Boolean(name);
            const username = typeof message?.username === "string" ? message.username : null;
            if (!byId.has(id) || resolved) {
                byId.set(id, {
                    name: name || null,
                    username,
                    avatarUrl,
                    resolved,
                });
            }
        });
        return byId;
    }, [messages]);

    const optionMetadata = useMemo(() => {
        const byId = new Map();
        (Array.isArray(userOptions) ? userOptions : []).forEach((user) => {
            const id = normalizeStringId(user?.userId ?? user?.id);
            if (!id) {
                return;
            }
            const name = firstTruthy(
                user?.displayName,
                user?.username,
                [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim()
            );
            const avatarUrl = user?.avatarUrl || user?.profilePicture || null;
            const username = typeof user?.username === "string" ? user.username : null;
            const resolved = Boolean(name || username || avatarUrl);
            byId.set(id, {
                name: name || null,
                username,
                avatarUrl,
                resolved,
            });
        });
        return byId;
    }, [userOptions]);

    useEffect(() => {
        const directory = directoryRef.current;
        const placeholders = [];
        const candidates = new Set([...memberIds, ...senderMetadata.keys()]);
        candidates.forEach((id) => {
            if (!directory.has(id)) {
                placeholders.push([id, createFallbackProfile(id)]);
            }
        });
        if (placeholders.length > 0) {
            mergeProfiles(placeholders);
        }
    }, [memberIds, senderMetadata, mergeProfiles]);

    useEffect(() => {
        if (memberProfiles.size === 0) {
            return;
        }
        mergeProfiles(Array.from(memberProfiles.entries()));
    }, [memberProfiles, mergeProfiles]);

    useEffect(() => {
        if (optionMetadata.size === 0) {
            return;
        }
        mergeProfiles(Array.from(optionMetadata.entries()));
    }, [optionMetadata, mergeProfiles]);

    useEffect(() => {
        if (senderMetadata.size === 0) {
            return;
        }
        mergeProfiles(Array.from(senderMetadata.entries()));
    }, [senderMetadata, mergeProfiles]);

    const fetchCandidates = useMemo(() => {
        const ids = new Set();
        senderMetadata.forEach((metadata, id) => {
            if (!metadata?.resolved) {
                ids.add(id);
            }
        });
        optionMetadata.forEach((metadata, id) => {
            if (!metadata?.resolved) {
                ids.add(id);
            }
        });
        return ids;
    }, [senderMetadata, optionMetadata]);

    useEffect(() => {
        if (fetchCandidates.size === 0) {
            return undefined;
        }
        const directory = directoryRef.current;
        const pendingLookups = pendingLookupsRef.current;
        const idsToFetch = Array.from(fetchCandidates).filter((id) => {
            if (!id || pendingLookups.has(id)) {
                return false;
            }
            if (currentUserId != null && String(currentUserId).trim() === id) {
                return false;
            }
            const entry = directory.get(id);
            return !(entry && (entry.resolved || entry.username || entry.avatarUrl));
        });
        if (idsToFetch.length === 0) {
            return undefined;
        }

        idsToFetch.forEach((id) => pendingLookups.add(id));
        let cancelled = false;

        (async () => {
            try {
                const responses = await Promise.all(
                    idsToFetch.map((id) => userService.fetchUserById(id).catch(() => null))
                );
                if (cancelled) {
                    return;
                }
                const fetched = responses
                    .map((user, index) => {
                        const rawId = idsToFetch[index];
                        if (!user) {
                            return null;
                        }
                        const id = normalizeStringId(rawId);
                        const name = firstTruthy(
                            user?.displayName,
                            user?.username,
                            [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim()
                        );
                        const avatarUrl = user?.avatarUrl
                            || user?.profilePicture
                            || user?.avatar
                            || user?.profile?.avatar
                            || null;
                        return [
                            id,
                            {
                                name: name || `User #${id}`,
                                username: typeof user?.username === "string" ? user.username : null,
                                avatarUrl,
                                resolved: true,
                            },
                        ];
                    })
                    .filter(Boolean);
                if (fetched.length > 0) {
                    mergeProfiles(fetched);
                }
            } finally {
                idsToFetch.forEach((id) => pendingLookups.delete(id));
            }
        })();

        return () => {
            cancelled = true;
            idsToFetch.forEach((id) => pendingLookups.delete(id));
        };
    }, [fetchCandidates, currentUserId, mergeProfiles]);

    const resolveUserName = useCallback((userId) => {
        const id = normalizeStringId(userId);
        if (!id) {
            return null;
        }
        const entry = directoryRef.current.get(id) || createFallbackProfile(id);
        const name = entry.name || entry.username || `User #${id}`;
        // Include version to ensure this callback's identity tracks directory updates.
        return {
            ...entry,
            name,
            ...(version ? {} : {}),
        };
    }, [version]);

    return resolveUserName;
}
